import React, { useRef, useState, useEffect } from "react";
import "./ImageModal.scss";
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import FlareIcon from '@material-ui/icons/Flare';

const DEFAULT_ZOOM_STEP = 0.3;
const DEFAULT_LARGE_ZOOM = 4;

function getXY(e) {
    let x = 0;
    let y = 0;
    if (e.touches && e.touches.length) {
        x = e.touches[0].pageX;
        y = e.touches[0].pageY;
    } else {
        x = e.pageX;
        y = e.pageY;
    }
    return { x, y };
}

const Cond = ({ condition, children }) => {
    return condition ? <>{children}</> : null;
};

const Lightbox = (props) => {
    const [state, setState] = useState({
        x: 0,
        y: 0,
        zoom: 1,
        rotate: 0,
        loading: true,
        moving: false,
        current: props.startIndex,
        multi: props.images?.length ? true : false,
        brightness: 100,
    });

    const initX = useRef(0);
    const initY = useRef(0);
    const lastX = useRef(0);
    const lastY = useRef(0);
    const contRef = useRef(null);

    const createTransform = (x, y, zoom, rotate) =>
        `translate3d(${x}px,${y}px,0px) scale(${zoom}) rotate(${rotate}deg)`;

    const stopSideEffect = (e) => e.stopPropagation();

    const getCurrentImage = (s, p) => {
        if (!s.multi) return p.image ?? "";
        return p.images[s.current]?.url ?? p.images?.[s.current] ?? "";
    };

    const getCurrentTitle = (s, p) => {
        if (!s.multi) return p.title ?? "";
        return p.images?.[s.current]?.title ?? "";
    };

    const resetZoom = () => setState({ ...state, x: 0, y: 0, zoom: 1 });

    const shockZoom = (e) => {
        const {
            zoomStep = DEFAULT_ZOOM_STEP,
            allowZoom = true,
            doubleClickZoom = DEFAULT_LARGE_ZOOM,
        } = props;
        if (!allowZoom || !doubleClickZoom) return false;
        stopSideEffect(e);
        if (state.zoom > 1) return resetZoom();
        const _z = ((zoomStep < 1) ? Math.ceil(doubleClickZoom / zoomStep) : zoomStep) * zoomStep;
        const _xy = getXY(e);
        const _cbr = contRef.current?.getBoundingClientRect?.();
        const _ccx = _cbr.x + (_cbr.width / 2);
        const _ccy = _cbr.y + (_cbr.height / 2);
        const x = (_xy.x - _ccx) * -1 * _z;
        const y = (_xy.y - _ccy) * -1 * _z;
        setState({ ...state, x, y, zoom: _z });
    };

    const navigateImage = (direction, e) => {
        stopSideEffect(e);
        let current = 0;
        switch (direction) {
            case "next":
                current = state.current + 1;
                break;
            case "prev":
                current = state.current - 1;
                break;
            default: {
                break;
            }
        }
        if (current >= props.images.length) current = 0;
        else if (current < 0) current = props.images.length - 1;
        setState({ ...state, current, x: 0, y: 0, zoom: 1, rotate: 0, loading: true });
        if (typeof props.onNavigateImage === 'function') {
            props.onNavigateImage(current);
        }
    };

    const startMove = (e) => {
        if (state.zoom <= 1) return false;
        setState({ ...state, moving: true });
        let xy = getXY(e);
        initX.current = xy.x - lastX.current;
        initY.current = xy.y - lastY.current;
    };

    const duringMove = (e) => {
        if (!state.moving) return false;
        let xy = getXY(e);
        lastX.current = xy.x - initX.current;
        lastY.current = xy.y - initY.current;
        setState({
            ...state,
            x: xy.x - initX.current,
            y: xy.y - initY.current
        });
    };

    const endMove = (e) => setState({ ...state, moving: false });

    const applyZoom = (type) => {
        let { zoomStep = DEFAULT_ZOOM_STEP } = props;
        switch (type) {
            case "in":
                setState({ ...state, zoom: state.zoom + zoomStep });
                break;
            case "out":
                let newZoom = state.zoom - zoomStep;
                if (newZoom < 1) break;
                else if (newZoom === 1) setState({ ...state, x: 0, y: 0, zoom: 1 });
                else setState({ ...state, zoom: newZoom });
                break;
            case "reset":
                resetZoom();
                break;
            default: {
                break;
            }
        }
    };

    const applyRotate = (type) => {
        switch (type) {
            case "cw":
                setState({ ...state, rotate: state.rotate + 90 });
                break;
            case "acw":
                setState({ ...state, rotate: state.rotate - 90 });
                break;
            default: {
                break;
            }
        }
    };

    const reset = (e) => {
        stopSideEffect(e);
        setState({ ...state, x: 0, y: 0, zoom: 1, rotate: 0 });
    };

    const exit = (e) => {
        if (typeof props.handleModal === "function") props.handleModal();
        console.error("No Exit function passed on prop: onClose. Clicking the close button will do nothing");
    };

    const shouldShowReset = () => (state.x || state.y || state.zoom !== 1 || state.rotate !== 0);

    const canvasClick = (e) => {
        let { clickOutsideToExit = true } = props;
        if (clickOutsideToExit && state.zoom <= 1) return exit(e);
    };

    const keyboardNavigation = (e) => {
        let { allowZoom = true, allowReset = true } = props;
        let { multi, x, y, zoom } = state;
        switch (e.key) {
            case "ArrowLeft":
                if (multi && zoom === 1) navigateImage("prev", e);
                else if (zoom > 1) setState({ ...state, x: x - 20 });
                break;
            case "ArrowRight":
                if (multi && zoom === 1) navigateImage("next", e);
                else if (zoom > 1) setState({ ...state, x: x + 20 });
                break;
            case "ArrowUp":
                if (zoom > 1) setState({ ...state, y: y + 20 });
                break;
            case "ArrowDown":
                if (zoom > 1) setState({ ...state, y: y - 20 });
                break;
            case "+":
                if (allowZoom) applyZoom("in");
                break;
            case "-":
                if (allowZoom) applyZoom("out");
                break;
            case "Escape":
                if (allowReset && shouldShowReset()) reset(e);
                else exit(e);
                break;
            default: {
                break;
            }
        }
    };

    useEffect(() => {
        document.body.classList.add("lb-open-lightbox");
        let { keyboardInteraction = true } = props;
        if (keyboardInteraction) document.addEventListener("keyup", keyboardNavigation);

        // setState({current:props.startIndex})
        setState({...state,current:props.startIndex})

        return () => {
            document.body.classList.remove("lb-open-lightbox");
            let { keyboardInteraction = true } = props;
            if (keyboardInteraction) document.removeEventListener("keyup", keyboardNavigation);
        };
    }, [props]);

    const handleChange = (event) => {
        const value = event.target.value;
        setState({ ...state, brightness: value });
    };

    let image = getCurrentImage(state, props);
    let title = getCurrentTitle(state, props);
    const { brightness } = state;

    function handleDownload(e) {
        e.preventDefault();
        const link = document.createElement('a');
        link.href = Array.isArray(image) ? image[0] : image;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    if (!image) {
        console.warn("Not showing lightbox because no image(s) was supplied");
        return null;
    }

    let {
        allowZoom = true,
        allowRotate = true,
        buttonAlign = "flex-end",
        showTitle = true,
        allowReset = true
    } = props;

    let _reset = allowReset && shouldShowReset();

    return (
        <div className="lb-container">
            <div className="lb-header" style={{ justifyContent: buttonAlign }}>
                <div className="brightness-div">
                    <FlareIcon style={{ color: '#fff' }} />
                    <input
                        style={{ marginLeft: '10px' }}
                        type="range"
                        min="40"
                        max="150"
                        value={brightness}
                        onChange={handleChange}
                    />
                </div>
                <Cond condition={showTitle && title}>
                    <div className="lb-title"
                        style={{
                            display: buttonAlign === "center" ? "none" : "flex",
                            order: buttonAlign === "flex-start" ? "2" : "unset"
                        }}>
                        <span title={title} style={{ textAlign: buttonAlign === "flex-start" ? "right" : "left" }}>{title}</span>
                    </div>
                </Cond>
                <Cond condition={buttonAlign === "center" || _reset}>
                    <div title="Reset"
                        style={{ order: buttonAlign === "flex-start" ? "1" : "unset" }}
                        className={`lb-button lb-icon-reset lb-hide-mobile reload ${_reset ? "" : "lb-disabled"}`}
                        onClick={reset}></div>
                </Cond>
                {props.images.length !== 1 ?
                    <Cond condition={state.multi}>
                        <div title="Previous" className="lb-button lb-icon-arrow prev lb-hide-mobile" onClick={e => navigateImage("prev", e)}></div>
                        <div title="Next" className="lb-button lb-icon-arrow next lb-hide-mobile" onClick={e => navigateImage("next", e)}></div>
                    </Cond> : ""
                }
                <Cond condition={allowZoom}>
                    <div title="Zoom In" className="lb-button lb-icon-zoomin zoomin" onClick={() => applyZoom("in")}></div>
                    <div title="Zoom Out"
                        className={`lb-button lb-icon-zoomout zoomout ${state.zoom <= 1 ? "lb-disabled" : ""}`}
                        onClick={() => applyZoom("out")}></div>
                </Cond>
                <Cond condition={allowRotate}>
                    <div title="Rotate left" className="lb-button lb-icon-rotate rotatel" onClick={() => applyRotate("acw")}></div>
                    <div title="Rotate right" className="lb-button lb-icon-rotate rotater" onClick={() => applyRotate("cw")}></div>
                </Cond>
                <div className="lb-button" onClick={(e) => handleDownload(e)}>
                    <CloudDownloadIcon style={{ color: '#fff' }} />
                </div>
                <div title="Close" className="lb-button lb-icon-close close" style={{ order: buttonAlign === "flex-start" ? "-1" : "unset" }} onClick={e => exit(e)}></div>
            </div>
            <div
                className={`lb-canvas${state.loading ? " lb-loading" : ""}`}
                ref={contRef}
                onClick={e => canvasClick(e)}>
                <img
                    draggable="false"
                    style={{
                        transform: createTransform(state.x, state.y, state.zoom, state.rotate),
                        cursor: state.zoom > 1 ? "grab" : "unset",
                        transition: state.moving ? "none" : "all 0.1s",
                        filter: `brightness(${brightness / 100})`
                    }}
                    onMouseDown={e => startMove(e)}
                    onTouchStart={e => startMove(e)}
                    onMouseMove={e => duringMove(e)}
                    onTouchMove={e => duringMove(e)}
                    onMouseUp={e => endMove(e)}
                    onMouseLeave={e => endMove(e)}
                    onTouchEnd={e => endMove(e)}
                    onClick={e => stopSideEffect(e)}
                    onDoubleClick={e => shockZoom(e)}
                    onLoad={e => setState({ ...state, loading: false })}
                    className={`lb-img${state.loading ? " lb-loading" : ""}`}
                    title={title}
                    src={image} alt={title} />
                <div className="mobile-controls lb-show-mobile">
                    {props.images.length !== 1 ?
                        <>
                                            {state.multi ? <div title="Previous" className="lb-button lb-icon-arrow prev" onClick={e => navigateImage("prev", e)}></div> : null}
                    {_reset ? <div title="Reset" className="lb-button lb-icon-reset reload" onClick={reset}></div> : null}
                    {state.multi ? <div title="Next" className="lb-button lb-icon-arrow next" onClick={e => navigateImage("next", e)}></div> : null}
                        </> : <></>}

                </div>
                <div className="brightness-div-mobile">
                    <FlareIcon style={{ color: '#fff' }} />
                    <input
                        className="mobile-input"
                        style={{ marginTop: '10px', writingMode: 'vertical-lr', transform: 'rotate(180deg)', height: '150px', marginInlineStart: '10px' }}
                        type="range"
                        min="40"
                        max="150"
                        value={brightness}
                        onChange={handleChange}
                    />
                </div>
            </div>
        </div>
    );
};

export default Lightbox;
